import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  UserPlus,
  Mail,
  BookOpen,
  CreditCard,
  Send,
  ArrowRight,
  Activity,
} from "lucide-react";
import type { DashboardActivity } from "@/types/index";

interface ActivityFeedProps {
  activities?: DashboardActivity[];
  isLoading?: boolean;
}

const activityIcons = {
  user_signup: { icon: UserPlus, color: "bg-blue-500/10 text-blue-500" },
  email_open: { icon: Mail, color: "bg-emerald-500/10 text-emerald-500" },
  article_read: { icon: BookOpen, color: "bg-violet-500/10 text-violet-500" },
  subscription: { icon: CreditCard, color: "bg-amber-500/10 text-amber-500" },
  email_sent: { icon: Send, color: "bg-rose-500/10 text-rose-500" },
};

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, x: -20 },
  show: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring" as const,
      stiffness: 300,
      damping: 24,
    },
  },
};

function getInitials(name: string | null | undefined, email: string): string {
  if (name) {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return email.slice(0, 2).toUpperCase();
}

function ActivityItem({ activity }: { activity: DashboardActivity }) {
  const config = activityIcons[activity.type];
  const Icon = config.icon;

  return (
    <motion.div
      variants={item}
      className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors duration-200 group cursor-default"
    >
      {/* Activity Type Icon */}
      <div className={cn("p-2 rounded-lg shrink-0", config.color)}>
        <Icon className="w-4 h-4" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {activity.user && (
            <Avatar className="h-5 w-5">
              <AvatarImage src={undefined} />
              <AvatarFallback className="text-[10px] bg-muted">
                {getInitials(activity.user.name, activity.user.email)}
              </AvatarFallback>
            </Avatar>
          )}
          <p className="text-sm text-foreground truncate font-medium">
            {activity.description}
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          {formatDistanceToNow(new Date(activity.timestamp), {
            addSuffix: true,
          })}
        </p>
      </div>

      {/* Hover indicator */}
      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
        <ArrowRight className="w-4 h-4 text-muted-foreground" />
      </div>
    </motion.div>
  );
}

function ActivityItemSkeleton() {
  return (
    <div className="flex items-start gap-4 p-3">
      <Skeleton className="h-8 w-8 rounded-lg" />
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded-full" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-3 w-24" />
      </div>
    </div>
  );
}

export function ActivityFeed({ activities, isLoading = false }: ActivityFeedProps) {
  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg font-semibold">Recent Activity</CardTitle>
            </div>
            <Skeleton className="h-8 w-20" />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <ActivityItemSkeleton key={i} />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <motion.div
              initial={{ rotate: 0 }}
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <Activity className="w-5 h-5 text-primary" />
            </motion.div>
            <CardTitle className="text-lg font-semibold">Recent Activity</CardTitle>
          </div>
          <Link
            to="/analytics"
            className="text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1 group"
          >
            View all
            <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {activities && activities.length > 0 ? (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="space-y-1"
          >
            {activities.slice(0, 8).map((activity) => (
              <ActivityItem key={activity.id} activity={activity} />
            ))}
          </motion.div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Activity className="w-8 h-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">No recent activity</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function ActivityFeedSkeleton() {
  return (
    <Card className="h-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-6 w-32" />
          </div>
          <Skeleton className="h-5 w-16" />
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1">
          {Array.from({ length: 6 }).map((_, i) => (
            <ActivityItemSkeleton key={i} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
